/**
 * RecurrenceSummaryPanel.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Painel direito do drawer de agendamento recorrente.
 *
 * Exibe:
 *  - Resumo da configuração (início, frequência, quantidade, total)
 *  - Lista de cards de ocorrências com badges de disponibilidade
 *  - Rodapé com contagem de agendamentos a criar
 *  - Aviso sobre revalidação no momento da criação
 *
 * Este componente é puramente apresentacional — toda a lógica fica no
 * AppointmentDrawer. Recebe as ocorrências e os callbacks de ação.
 *
 * @module RecurrenceSummaryPanel
 */

import React from 'react';
import {
  INTERVAL_OPTIONS,
  OCCURRENCE_STATUS,
  formatOccurrenceDate,
  getOccurrenceState,
  summarizeOccurrences,
  parseDateKey,
} from '../../utils/recurrenceUtils';
import styles from './RecurrenceSummaryPanel.module.css';

// ── Ícones SVG inline ─────────────────────────────────────────────────────────

const CalendarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

const RepeatIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="17 1 21 5 17 9"/>
    <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
    <polyline points="7 23 3 19 7 15"/>
    <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
  </svg>
);

const CountIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" y1="6" x2="21" y2="6"/>
    <line x1="8" y1="12" x2="21" y2="12"/>
    <line x1="8" y1="18" x2="21" y2="18"/>
    <line x1="3" y1="6" x2="3.01" y2="6"/>
    <line x1="3" y1="12" x2="3.01" y2="12"/>
    <line x1="3" y1="18" x2="3.01" y2="18"/>
  </svg>
);

const TotalIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
  </svg>
);

const ClockIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const XCircleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="15" y1="9" x2="9" y2="15"/>
    <line x1="9" y1="9" x2="15" y2="15"/>
  </svg>
);

const MinusCircleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="8" y1="12" x2="16" y2="12"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);

const LightbulbIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="9" y1="18" x2="15" y2="18"/>
    <line x1="10" y1="22" x2="14" y2="22"/>
    <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/>
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

// ── Helpers ───────────────────────────────────────────────────────────────────

const WEEKDAY_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const WEEKDAY_LONG  = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
const MONTH_NAMES   = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

function formatCardDate(dateKey) {
  const date = parseDateKey(dateKey);
  const weekday = WEEKDAY_LONG[date.getDay()];
  const day     = String(date.getDate()).padStart(2, '0');
  const month   = String(date.getMonth() + 1).padStart(2, '0');
  const year    = date.getFullYear();
  return { weekday, date: `${day}/${month}/${year}` };
}

function formatSummaryStart(slot, intervalDays) {
  if (!slot) return '—';
  const date = slot.date;
  const day   = String(date.getDate()).padStart(2, '0');
  const month = MONTH_NAMES[date.getMonth()];
  const year  = date.getFullYear();
  const h     = String(slot.hour).padStart(2, '0');
  const m     = String(slot.minute).padStart(2, '0');
  return `${day}/${month}/${year} às ${h}:${m}`;
}

function getIntervalLabel(intervalDays) {
  const opt = INTERVAL_OPTIONS.find(o => o.days === intervalDays);
  return opt ? opt.label : `A cada ${intervalDays} dias`;
}

// ── Badge de estado ───────────────────────────────────────────────────────────

const STATE_BADGE = {
  available:   { label: 'Disponível',      className: 'badgeAvailable' },
  changed:     { label: 'Horário alterado', className: 'badgeChanged' },
  skipped:     { label: 'Ignorado',        className: 'badgeSkipped' },
  closed:      { label: 'Clínica fechada', className: 'badgeConflict' },
  unavailable: { label: 'Indisponível',    className: 'badgeConflict' },
};

// Horário fora da regra do profissional: dá para criar, mas avisa (prévia manda `warning`)
const WARNING_BADGE = {
  off_hours: { label: 'Fora do horário', className: 'badgeWarning' },
  limit:     { label: 'No limite do profissional', className: 'badgeWarning' },
};

function OccurrenceBadge({ state, warning }) {
  const badge = (state === 'available' && WARNING_BADGE[warning]) || STATE_BADGE[state] || STATE_BADGE.unavailable;
  return (
    <span className={`${styles.badge} ${styles[badge.className]}`}>
      {badge.label}
    </span>
  );
}

// ── Ícone de estado ───────────────────────────────────────────────────────────

function StateIcon({ state }) {
  if (state === 'available' || state === 'changed') {
    return <span className={styles.iconAvailable}><CheckCircleIcon /></span>;
  }
  if (state === 'skipped') {
    return <span className={styles.iconSkipped}><MinusCircleIcon /></span>;
  }
  return <span className={styles.iconConflict}><XCircleIcon /></span>;
}

// ── Card de ocorrência ────────────────────────────────────────────────────────

function OccurrenceCard({ occ, index, onToggleSkip, onSelectAlternative }) {
  const state = getOccurrenceState(occ);
  const isConflict = state === 'unavailable' || state === 'closed';
  const isSkipped  = state === 'skipped';
  const hasAlternatives = occ.alternatives?.length > 0;
  const { weekday, date } = formatCardDate(occ.date);
  const displayTime = occ.selectedTime || occ.time;

  return (
    <div
      className={[
        styles.occurrenceCard,
        isConflict ? styles.occurrenceCardConflict : '',
        isSkipped  ? styles.occurrenceCardSkipped  : '',
        state === 'available' || state === 'changed' ? styles.occurrenceCardAvailable : '',
      ].filter(Boolean).join(' ')}
    >
      {/* Barra lateral colorida */}
      <div className={[
        styles.cardAccent,
        isConflict ? styles.cardAccentConflict : '',
        isSkipped  ? styles.cardAccentSkipped  : '',
        !isConflict && !isSkipped ? styles.cardAccentAvailable : '',
      ].filter(Boolean).join(' ')} />

      <div className={styles.cardContent}>
        {/* Linha única: ícone + dia da semana + data + horário + badge */}
        <div className={styles.cardHeader}>
          <div className={styles.cardHeaderLeft}>
            <StateIcon state={state} />
            <div className={styles.cardDateInfo}>
              <span className={styles.cardWeekday}>{weekday}</span>
              <span className={styles.cardDate}>{date}</span>
            </div>
            <div className={styles.cardTime}>
              <span className={styles.cardTimeIcon}><ClockIcon /></span>
              <span className={styles.cardTimeValue}>{displayTime}</span>
            </div>
          </div>
          <OccurrenceBadge state={state} warning={occ.warning} />
        </div>

        {/* Ações de conflito */}
        {isConflict && !isSkipped && (
          <div className={styles.cardConflictActions}>
            {hasAlternatives ? (
              <select
                className={styles.altSelect}
                value={occ.selectedTime || ''}
                onChange={(e) => onSelectAlternative(index, e.target.value)}
              >
                <option value="">Escolher outro horário...</option>
                {occ.alternatives.map((time) => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
            ) : (
              <span className={styles.noAlternatives}>
                Nenhum horário livre neste dia
              </span>
            )}
          </div>
        )}

        {/* Ação ignorar / incluir — apenas para conflitos ou ignorados */}
        {(isConflict || isSkipped) && (
          <div className={styles.cardFooterActions}>
            {!isSkipped ? (
              <button
                type="button"
                className={styles.btnIgnore}
                onClick={() => onToggleSkip(index)}
              >
                <TrashIcon />
                Ignorar esta data
              </button>
            ) : (
              <button
                type="button"
                className={styles.btnReinclude}
                onClick={() => onToggleSkip(index)}
              >
                Incluir novamente
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────

/**
 * Painel de resumo da recorrência (coluna direita do drawer).
 *
 * @param {Object} props
 * @param {Object} props.slot - { date: Date, hour: number, minute: number }
 * @param {number} props.intervalDays
 * @param {number} props.occurrencesCount
 * @param {Array|null} props.occurrences - Ocorrências com decisões aplicadas
 * @param {Function} props.onToggleSkip - (index) => void
 * @param {Function} props.onSelectAlternative - (index, time) => void
 * @param {boolean} props.isChecking
 * @param {Object|null} props.result - Resultado da criação em lote
 */
export default function RecurrenceSummaryPanel({
  slot,
  intervalDays,
  occurrencesCount,
  occurrences,
  onToggleSkip,
  onSelectAlternative,
  isChecking = false,
  result = null,
}) {
  const summary = occurrences ? summarizeOccurrences(occurrences) : null;

  return (
    <div className={styles.panel}>
      {/* ── Título do painel ──────────────────────────────────────────────── */}
      <div className={styles.panelHeader}>
        <h3 className={styles.panelTitle}>Resumo da recorrência</h3>
      </div>

      {/* ── Resumo da configuração ────────────────────────────────────────── */}
      <div className={styles.summaryGrid}>
        <div className={styles.summaryRow}>
          <span className={styles.summaryIcon}><CalendarIcon /></span>
          <span className={styles.summaryLabel}>Início</span>
          <span className={styles.summaryValue}>{formatSummaryStart(slot, intervalDays)}</span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryIcon}><RepeatIcon /></span>
          <span className={styles.summaryLabel}>Repetir</span>
          <span className={styles.summaryValue}>{getIntervalLabel(intervalDays)}</span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryIcon}><CountIcon /></span>
          <span className={styles.summaryLabel}>Quantidade</span>
          <span className={styles.summaryValue}>{occurrencesCount} agendamentos</span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryIcon}><TotalIcon /></span>
          <span className={styles.summaryLabel}>Total previsto</span>
          <span className={styles.summaryValue}>
            {occurrences ? `${occurrences.length} datas` : `${occurrencesCount} datas`}
          </span>
        </div>
      </div>

      {/* ── Divisor ───────────────────────────────────────────────────────── */}
      <div className={styles.divider} />

      {/* ── Lista de ocorrências ──────────────────────────────────────────── */}
      <div className={styles.occurrencesSection}>
        <div className={styles.occurrencesSectionHeader}>
          <h4 className={styles.occurrencesSectionTitle}>Datas previstas</h4>
          {occurrences && (
            <span className={styles.occurrencesSectionCount}>
              {occurrences.length} datas
            </span>
          )}
        </div>

        {!occurrences && !isChecking && (
          <div className={styles.emptyState}>
            <p className={styles.emptyStateText}>
              Configure a recorrência e clique em <strong>Recalcular datas</strong> para visualizar as ocorrências.
            </p>
          </div>
        )}

        {isChecking && (
          <div className={styles.loadingState}>
            <span className={styles.loadingSpinner} />
            <span className={styles.loadingText}>Verificando disponibilidade...</span>
          </div>
        )}

        {occurrences && !isChecking && (
          <>
            <p className={styles.occurrencesHint}>
              Revise as datas abaixo. Você pode ignorar ou escolher outro horário para os dias indisponíveis.
            </p>
            <div className={styles.occurrenceList}>
              {occurrences.map((occ, index) => (
                <OccurrenceCard
                  key={`${occ.date}-${index}`}
                  occ={occ}
                  index={index}
                  onToggleSkip={onToggleSkip}
                  onSelectAlternative={onSelectAlternative}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── Rodapé com contagem ───────────────────────────────────────────── */}
      {summary && (
        <>
          <div className={styles.divider} />
          <div className={styles.summaryFooter}>
            {/* Resultado da criação em lote */}
            {result ? (
              <div className={[
                styles.resultBox,
                result.status === 'SUCCESS' ? styles.resultSuccess :
                result.status === 'PARTIAL' ? styles.resultPartial :
                styles.resultError
              ].filter(Boolean).join(' ')}>
                <span className={styles.resultCount}>
                  {result.created?.length || 0} agendamento{(result.created?.length || 0) !== 1 ? 's' : ''} criado{(result.created?.length || 0) !== 1 ? 's' : ''}
                </span>
                {result.failed?.length > 0 && (
                  <span className={styles.resultFailed}>
                    {result.failed.length} não pôde{result.failed.length !== 1 ? 'm' : ''} ser criado{result.failed.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            ) : (
              /* Prévia antes da criação */
              <div className={styles.creationPreview}>
                {summary.toCreate > 0 && (
                  <div className={styles.previewRow}>
                    <span className={styles.previewIconGreen}>
                      <CheckCircleIcon />
                    </span>
                    <span className={styles.previewTextGreen}>
                      <strong>{summary.toCreate}</strong> agendamento{summary.toCreate !== 1 ? 's' : ''} {summary.toCreate !== 1 ? 'serão criados' : 'será criado'}
                    </span>
                  </div>
                )}
                {summary.skipped > 0 && (
                  <div className={styles.previewRow}>
                    <span className={styles.previewIconGray}>
                      <MinusCircleIcon />
                    </span>
                    <span className={styles.previewTextGray}>
                      {summary.skipped} data{summary.skipped !== 1 ? 's' : ''} {summary.skipped !== 1 ? 'serão ignoradas' : 'será ignorada'}
                    </span>
                  </div>
                )}
                {summary.unresolved > 0 && (
                  <div className={styles.previewRow}>
                    <span className={styles.previewIconRed}>
                      <XCircleIcon />
                    </span>
                    <span className={styles.previewTextRed}>
                      {summary.unresolved} conflito{summary.unresolved !== 1 ? 's' : ''} pendente{summary.unresolved !== 1 ? 's' : ''}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Aviso importante ──────────────────────────────────────────── */}
          {!result && (
            <div className={styles.importantNote}>
              <span className={styles.importantNoteIcon}><LightbulbIcon /></span>
              <div className={styles.importantNoteContent}>
                <span className={styles.importantNoteTitle}>Importante</span>
                <span className={styles.importantNoteText}>
                  A disponibilidade dos horários será verificada novamente no momento da criação. Caso algum horário seja ocupado, avisaremos quais não puderam ser agendados.
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
